import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, deleteDoc, updateDoc, query, orderBy, deleteField, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { FaTrash, FaBan, FaUnlock, FaArrowUp, FaArrowLeft, FaSearch, FaWhatsapp, FaTimes, FaExclamationTriangle, FaCheckCircle, FaEdit, FaSave } from 'react-icons/fa';
import './GerenciarUsuarios.css';
import avatar from './avatar.png';
import { normalizeText } from '../utils/text';
import { calculatePlanExpirationDate } from '../utils/planDuration';
import { parsePlanExpirationDate } from '../utils/effectivePlan';

function GerenciarUsuarios() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);

  // Estados das modais
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    action: null,
    actionText: '',
    cancelText: 'Cancelar'
  });

  const navigate = useNavigate();

  const filterUsersByQuery = (list, queryText) => {
    const normalizedQuery = normalizeText(queryText);
    if (!normalizedQuery) return list;
    return list.filter(user =>
      normalizeText(user.name).includes(normalizedQuery) ||
      normalizeText(user.email).includes(normalizedQuery) ||
      normalizeText(user.phone).includes(normalizedQuery)
    );
  };

  const findPlanByName = (planName) => (
    plans.find(plan => normalizeText(plan.name) === normalizeText(planName))
  );

  const findPlanById = (planId) => plans.find(plan => plan.id === planId);

  const isFreePlan = (plan) => normalizeText(plan?.planType) === 'free';

  const getUserPlanSelection = (user) => {
    const storedPlanName = user.planDetails?.name || user.plano || user.plan;
    const catalogPlan = findPlanByName(storedPlanName);

    if (catalogPlan && !isFreePlan(catalogPlan)) return catalogPlan.id;
    if (normalizeText(user.plano || user.plan) === 'free') return 'Free';

    return storedPlanName ? `legacy:${storedPlanName}` : 'Free';
  };

  const toDateInputValue = (value) => {
    const date = parsePlanExpirationDate(value);
    if (!date) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const toExpirationTimestamp = (value) => {
    if (!value) return null;

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;

    const expirationDate = new Date(year, month - 1, day, 23, 59, 59, 999);
    return Number.isNaN(expirationDate.getTime())
      ? null
      : Timestamp.fromDate(expirationDate);
  };

  const timestampToPlanDateInput = (timestamp) => toDateInputValue(timestamp);

  const getDefaultPlanExpirationInput = (planId) => {
    if (planId === 'Free') return '';

    const selectedPlan = findPlanById(planId);
    const expirationDate = calculatePlanExpirationDate(selectedPlan?.duration);

    return toDateInputValue(expirationDate);
  };

  const handleEditPlanChange = (selectedPlanId) => {
    setEditUser({
      ...editUser,
      selectedPlanId,
      plano: selectedPlanId === 'Free' ? 'Free' : 'Premium',
      planExpirationInput: getDefaultPlanExpirationInput(selectedPlanId)
    });
  };

  const buildManualPlanUpdate = (selectedPlanId, expirationInput) => {
    if (selectedPlanId === 'Free') {
      return {
        plano: 'Free',
        planExpirationDate: null,
        planDetails: null,
        updateData: {
          plano: 'Free',
          planExpirationDate: deleteField(),
          planDetails: deleteField()
        }
      };
    }

    const selectedPlan = findPlanById(selectedPlanId);
    if (!selectedPlan || isFreePlan(selectedPlan)) {
      throw new Error('Selecione um plano premium válido.');
    }

    const startTimestamp = Timestamp.now();
    const expirationDate = calculatePlanExpirationDate(selectedPlan?.duration);
    const expirationTimestamp = expirationInput !== undefined
      ? toExpirationTimestamp(expirationInput)
      : expirationDate
        ? Timestamp.fromDate(expirationDate)
        : null;

    return {
      plano: 'Premium',
      planExpirationDate: expirationTimestamp,
      planDetails: {
        name: selectedPlan.name,
        type: selectedPlan?.planType || 'premium',
        duration: selectedPlan?.duration || 'mensal',
        source: 'manual',
        startDate: startTimestamp,
        expirationDate: expirationTimestamp
      },
      updateData: {
        // Mantém a mesma estrutura legada usada pelo webhook: o nível de
        // acesso fica em `plano` e o produto contratado em `planDetails`.
        plano: 'Premium',
        planExpirationDate: expirationTimestamp || deleteField(),
        planDetails: {
          name: selectedPlan.name,
          type: selectedPlan?.planType || 'premium',
          duration: selectedPlan?.duration || 'mensal',
          source: 'manual',
          startDate: startTimestamp,
          expirationDate: expirationTimestamp
        }
      }
    };
  };

  const applyManualExpirationOverride = (updateData, editUserData) => {
    if (updateData.plano === 'Free') {
      updateData.planExpirationDate = deleteField();
      updateData.planDetails = deleteField();
      return {
        planExpirationDate: null,
        planDetails: null
      };
    }

    const nextExpirationTimestamp = toExpirationTimestamp(editUserData.planExpirationInput);

    if (nextExpirationTimestamp) {
      updateData.planExpirationDate = nextExpirationTimestamp;
    } else {
      updateData.planExpirationDate = deleteField();
    }

    const nextPlanDetails = updateData.planDetails && typeof updateData.planDetails === 'object'
      ? {
        ...updateData.planDetails,
        expirationDate: nextExpirationTimestamp
      }
      : editUserData.planDetails
        ? {
          ...editUserData.planDetails,
          expirationDate: nextExpirationTimestamp
        }
        : null;

    if (nextPlanDetails) {
      updateData.planDetails = nextPlanDetails;
    }

    return {
      planExpirationDate: nextExpirationTimestamp,
      planDetails: nextPlanDetails
    };
  };

  // Funções das modais
  const showConfirmation = (title, message, action, actionText = 'Confirmar') => {
    setModalConfig({
      title,
      message,
      action,
      actionText,
      cancelText: 'Cancelar'
    });
    setShowConfirmModal(true);
  };

  const showSuccess = (message) => {
    setModalConfig({ ...modalConfig, message });
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 3000);
  };

  const showErrorMessage = (message) => {
    setModalConfig({ ...modalConfig, message });
    setShowErrorModal(true);
    setTimeout(() => setShowErrorModal(false), 4000);
  };

  const handleConfirm = () => {
    if (modalConfig.action) {
      modalConfig.action();
    }
    setShowConfirmModal(false);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setModalConfig({ ...modalConfig, action: null });
  };

  const fetchPlans = async () => {
    try {
      const plansRef = collection(db, 'planos_assinatura');
      const plansQuery = query(plansRef, orderBy('displayOrder', 'asc'));
      const snapshot = await getDocs(plansQuery);
      const plansList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlans(plansList);
    } catch (err) {
      console.error('Erro ao buscar planos:', err);
    }
  };

  // Função para buscar usuários do Firestore
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const querySnapshot = await Promise.race([
        getDocs(collection(db, "usuarios")),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout ao carregar usuários.')), 12000)
        )
      ]);
      const usersData = [];

      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        const createdAtDate = userData.createdAt?.toDate
          ? userData.createdAt.toDate()
          : userData.createdAt?.seconds
            ? new Date(userData.createdAt.seconds * 1000)
            : null;

        usersData.push({
          id: doc.id,
          name: userData.name || 'Nome não informado',
          email: userData.email || 'Email não informado',
          createdAt: createdAtDate
            ? createdAtDate.toLocaleDateString('pt-BR')
            :
            'Data não informada',
          createdAtRaw: createdAtDate ? createdAtDate.getTime() : 0,
          plan: userData.plano || 'Free',
          plano: userData.plano || 'Free',
          phone: userData.whatsapp || userData.phone || null,
          whatsapp: userData.whatsapp || '',
          photo: userData.photoURL || avatar,
          photoURL: userData.photoURL || '',
          blocked: userData.blocked || false,
          provider: userData.provider || 'email',
          role: userData.role || '',
          active: userData.active !== undefined ? userData.active : true,
          planExpirationDate: userData.planExpirationDate || null,
          planDetails: userData.planDetails || null
        });
      });

      // Ordenar por data de criação (mais recentes primeiro)
      usersData.sort((a, b) => b.createdAtRaw - a.createdAtRaw);

      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (err) {
      console.error('Erro ao buscar usuários:', err);
      setError('Erro ao carregar usuários. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPlans();

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setFilteredUsers(filterUsersByQuery(users, searchQuery));
  }, [searchQuery, users]);

  const handleDelete = async (userId, userName) => {
    const deleteAction = async () => {
      try {
        await deleteDoc(doc(db, "usuarios", userId));

        const updatedUsers = users.filter(user => user.id !== userId);
        setUsers(updatedUsers);
        setFilteredUsers(filterUsersByQuery(updatedUsers, searchQuery));

        showSuccess(`Usuário "${userName}" foi excluído com sucesso!`);
      } catch (err) {
        console.error('Erro ao excluir usuário:', err);
        showErrorMessage('Erro ao excluir usuário. Tente novamente.');
      }
    };

    showConfirmation(
      'Excluir Usuário',
      `Tem certeza que deseja excluir o usuário "${userName}"? Esta ação não pode ser desfeita.`,
      deleteAction,
      'Excluir'
    );
  };

  const handleBlockToggle = async (userId, blocked, userName) => {
    const action = blocked ? 'desbloquear' : 'bloquear';

    const blockAction = async () => {
      try {
        await updateDoc(doc(db, "usuarios", userId), {
          blocked: !blocked
        });

        const updatedUsers = users.map(user =>
          user.id === userId ? { ...user, blocked: !blocked } : user
        );
        setUsers(updatedUsers);
        setFilteredUsers(filterUsersByQuery(updatedUsers, searchQuery));

        const statusText = action === 'bloquear' ? 'bloqueado' : 'desbloqueado';
        showSuccess(`Usuário "${userName}" foi ${statusText} com sucesso!`);
      } catch (err) {
        console.error('Erro ao atualizar status do usuário:', err);
        showErrorMessage('Erro ao atualizar status do usuário. Tente novamente.');
      }
    };

    showConfirmation(
      `${action.charAt(0).toUpperCase() + action.slice(1)} Usuário`,
      `Tem certeza que deseja ${action} o usuário "${userName}"?`,
      blockAction,
      action.charAt(0).toUpperCase() + action.slice(1)
    );
  };

  const openEditModal = (user) => {
    const selectedPlanId = getUserPlanSelection(user);

    setEditUser({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      plano: user.plano || user.plan || 'Free',
      whatsapp: user.whatsapp || '',
      phone: user.phone || '',
      blocked: !!user.blocked,
      especialista: user.role === 'especialista',
      originalRole: user.role || '',
      active: user.active !== undefined ? user.active : true,
      originalPlano: user.plano || user.plan || 'Free',
      selectedPlanId,
      originalSelectedPlanId: selectedPlanId,
      planExpirationDate: user.planExpirationDate || null,
      planDetails: user.planDetails || null,
      originalPlanExpirationInput: timestampToPlanDateInput(user.planExpirationDate),
      planExpirationInput: timestampToPlanDateInput(user.planExpirationDate)
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editUser) return;

    setSavingEdit(true);
    try {
      const userRef = doc(db, 'usuarios', editUser.id);
      const updateData = {
        name: editUser.name.trim() || 'Nome não informado',
        email: editUser.email.trim() || 'Email não informado',
        plano: editUser.plano || 'Free',
        whatsapp: editUser.whatsapp || '',
        phone: editUser.phone || '',
        blocked: !!editUser.blocked,
        active: editUser.active !== undefined ? editUser.active : true,
        updatedAt: Timestamp.now()
      };

      if (editUser.originalRole !== 'admin') {
        if (editUser.especialista) {
          updateData.role = 'especialista';
        } else {
          updateData.role = deleteField();
        }
      }

      const nextRole = editUser.originalRole === 'admin'
        ? 'admin'
        : editUser.especialista ? 'especialista' : '';

      let nextPlanExpirationDate = editUser.planExpirationDate || null;
      let nextPlanDetails = editUser.planDetails || null;
      const planChanged = editUser.originalSelectedPlanId !== editUser.selectedPlanId;
      const expirationChanged =
        editUser.originalPlanExpirationInput !== editUser.planExpirationInput;

      if (planChanged) {
        const manualPlanUpdate = buildManualPlanUpdate(
          editUser.selectedPlanId,
          editUser.planExpirationInput
        );
        Object.assign(updateData, manualPlanUpdate.updateData);
        updateData.plano = manualPlanUpdate.plano;
        nextPlanExpirationDate = manualPlanUpdate.planExpirationDate;
        nextPlanDetails = manualPlanUpdate.planDetails;
      }

      if (!planChanged && expirationChanged) {
        const manualExpirationUpdate = applyManualExpirationOverride(updateData, editUser);
        nextPlanExpirationDate = manualExpirationUpdate.planExpirationDate;
        nextPlanDetails = manualExpirationUpdate.planDetails;
      }

      await updateDoc(userRef, updateData);

      const updatedUsers = users.map(user =>
        user.id === editUser.id
          ? {
            ...user,
            name: updateData.name,
            email: updateData.email,
            plan: updateData.plano,
            plano: updateData.plano,
            whatsapp: updateData.whatsapp,
            phone: updateData.whatsapp || updateData.phone || null,
            blocked: updateData.blocked,
            role: nextRole,
            active: updateData.active,
            planExpirationDate: nextPlanExpirationDate,
            planDetails: nextPlanDetails
          }
          : user
      );

      setUsers(updatedUsers);
      setFilteredUsers(filterUsersByQuery(updatedUsers, searchQuery));

      setShowEditModal(false);
      setEditUser(null);
      showSuccess('Usuário atualizado com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar usuário:', err);
      showErrorMessage(err.message || 'Erro ao atualizar usuário. Tente novamente.');
    } finally {
      setSavingEdit(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="gerenciar-usuarios admin-panel-container">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTopColor: '#209cd3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p>Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gerenciar-usuarios admin-panel-container">
      <header className="admin-header">
        <button className="back-button" onClick={() => navigate('/admin-panel')}>
          <FaArrowLeft className="icon" /> Voltar
        </button>
        <div className="header-title">
          <h1>Gerenciar Usuários</h1>
          <p>Conecta EAD - Controle total dos usuários do sistema</p>
        </div>
      </header>

      <div className="admin-content">
        <div className="admin-list">
          <div className="list-header">
            <h2>Lista de Usuários ({filteredUsers.length})</h2>
            <div className="search-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por nome, e-mail ou telefone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {error && (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#dc2626'
            }}>
              <p>{error}</p>
              <button
                onClick={fetchUsers}
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1.5rem',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Tentar novamente
              </button>
            </div>
          )}

          <div className="admin-table">
            <div className="table-header">
              <span>Foto</span>
              <span>Nome</span>
              <span>E-mail</span>
              <span>Data de Cadastro</span>
              <span>Plano</span>
              <span>WhatsApp</span>
              <span>Ações</span>
            </div>
            {filteredUsers.map(user => (
              <div key={user.id} className="table-row">
                <span>
                  <img
                    src={user.photo}
                    alt="Foto de Perfil"
                    className="profile-photo"
                    onError={(e) => {
                      e.target.src = avatar;
                    }}
                  />
                </span>
                <span>{user.name}</span>
                <span className="user-email" title={user.email}>
                  {user.email}
                </span>
                <span>{user.createdAt}</span>
                <span>{user.planDetails?.name || user.plan}</span>
                <span>
                  {user.phone ? (
                    <a
                      href={`https://wa.me/${user.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whatsapp-link"
                    >
                      <FaWhatsapp className="icon" /> {user.phone}
                    </a>
                  ) : (
                    'Não informado'
                  )}
                </span>
                <span className="actions">
                  <button onClick={() => openEditModal(user)} className="edit">
                    <FaEdit className="icon" />
                    <span className="button-text-full">Editar</span>
                  </button>
                  <button onClick={() => handleDelete(user.id, user.name)} className="delete">
                    <FaTrash className="icon" />
                    <span className="button-text-full">Deletar</span>
                  </button>
                  <button
                    onClick={() => handleBlockToggle(user.id, user.blocked, user.name)}
                    className={user.blocked ? 'unblock' : 'block'}
                  >
                    {user.blocked ? <FaUnlock className="icon" /> : <FaBan className="icon" />}
                    <span className="button-text-full">
                      {user.blocked ? 'Desbloquear' : 'Bloquear'}
                    </span>
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showBackToTop && (
        <button className="back-to-top" onClick={scrollToTop}>
          <FaArrowUp className="icon" /> Voltar ao Topo
        </button>
      )}

      {/* Modal de Confirmação */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-icon warning">
                <FaExclamationTriangle />
              </div>
              <h3>{modalConfig.title}</h3>
              <button className="modal-close" onClick={handleCancel}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>{modalConfig.message}</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCancel}>
                {modalConfig.cancelText}
              </button>
              <button className="btn-confirm" onClick={handleConfirm}>
                {modalConfig.actionText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="modal-content success">
            <div className="modal-header">
              <div className="modal-icon success">
                <FaCheckCircle />
              </div>
              <h3>Sucesso!</h3>
              <button className="modal-close" onClick={() => setShowSuccessModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>{modalConfig.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Erro */}
      {showErrorModal && (
        <div className="modal-overlay">
          <div className="modal-content error">
            <div className="modal-header">
              <div className="modal-icon error">
                <FaExclamationTriangle />
              </div>
              <h3>Erro!</h3>
              <button className="modal-close" onClick={() => setShowErrorModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>{modalConfig.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && editUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content edit-user" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar Usuário</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="edit-user-form">
              <div className="form-row">
                <label>Nome</label>
                <input
                  type="text"
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>E-mail</label>
                <input
                  type="email"
                  value={editUser.email}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Plano</label>
                <select
                  value={editUser.selectedPlanId}
                  onChange={(e) => handleEditPlanChange(e.target.value)}
                >
                  <option value="Free">Free</option>
                  {String(editUser.selectedPlanId).startsWith('legacy:') && (
                    <option value={editUser.selectedPlanId}>
                      {editUser.planDetails?.name || editUser.originalPlano} (fora do catálogo)
                    </option>
                  )}
                  {plans.filter(plan => !isFreePlan(plan)).map(plan => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </select>
              </div>
              {editUser.selectedPlanId !== 'Free' && (
                <div className="form-row">
                  <label>Expiração do Plano</label>
                  <input
                    type="date"
                    value={editUser.planExpirationInput || ''}
                    onChange={(e) => setEditUser({
                      ...editUser,
                      planExpirationInput: e.target.value
                    })}
                  />
                </div>
              )}
              <div className="form-row">
                <label>WhatsApp</label>
                <input
                  type="text"
                  value={editUser.whatsapp}
                  onChange={(e) => setEditUser({ ...editUser, whatsapp: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>Telefone</label>
                <input
                  type="text"
                  value={editUser.phone}
                  onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
                />
              </div>
              <div className="form-row checkbox-row">
                <label>
                  <input
                    type="checkbox"
                    checked={editUser.especialista}
                    disabled={editUser.originalRole === 'admin'}
                    onChange={(e) => setEditUser({ ...editUser, especialista: e.target.checked })}
                  />
                  Especialista
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={editUser.active}
                    onChange={(e) => setEditUser({ ...editUser, active: e.target.checked })}
                  />
                  Ativo
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={editUser.blocked}
                    onChange={(e) => setEditUser({ ...editUser, blocked: e.target.checked })}
                  />
                  Bloqueado
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-confirm" disabled={savingEdit}>
                  {savingEdit ? 'Salvando...' : <><FaSave className="icon" /> Salvar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GerenciarUsuarios;
